import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import "../styles/AdminUsersPage.css";
import { API_BASE } from "../config";
import { getSession } from "../session";
import apiFetch from "../apiFetch";

const GAME_LABELS = {
  comprehension:      "Comprehension",
  mcq:                "MCQ Master",
  ranked:             "Ranked",
  book_comprehension: "Book Comprehension",
  vocab_definitions:  "Vocab: Definitions",
  vocab_synonyms:     "Vocab: Synonyms",
  vocab_antonyms:     "Vocab: Antonyms",
  vocab_mixed:        "Vocab: Mixed",
  synonyms_game:      "Synonyms Game",
  antonyms_game:      "Antonyms Game",
  vocab_matching:     "Vocab Matching",
};

function gameLabel(type) {
  return GAME_LABELS[type] || type;
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtDateShort(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "2-digit",
  });
}

function fmtDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function SummaryCard({ label, value, color }) {
  return (
    <div className={`rpt-card rpt-card--${color}`}>
      <p className="rpt-card-value">{value ?? "—"}</p>
      <p className="rpt-card-label">{label}</p>
    </div>
  );
}

export default function AdminUserReportPage() {
  const { id } = useParams();
  const [data,   setData]   = useState(null);
  const [status, setStatus] = useState("loading");

  const session = getSession();
  const uid = session?.userId ?? "";

  const fetchReport = async () => {
    setStatus("loading");
    try {
      const res = await apiFetch(
        `/api/admin/report/${id}?adminUserId=${uid}`,
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.status === 401 || res.status === 403) { setStatus("denied"); return; }
      if (res.status === 404) { setStatus("notfound"); return; }
      if (!res.ok) { setStatus("error"); return; }
      setData(await res.json());
      setStatus("ok");
    } catch {
      setStatus("error");
    }
  };

  useEffect(() => {
    if (!session) { setStatus("denied"); return; }
    fetchReport();
  }, [id]); // eslint-disable-line

  if (status === "loading") {
    return <div className="adm-page"><div className="adm-center"><p className="adm-muted">Loading…</p></div></div>;
  }

  if (status === "denied") {
    return (
      <div className="adm-page">
        <div className="adm-center">
          <div className="adm-denied-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h2 className="adm-denied-title">Access Denied</h2>
          <p className="adm-muted">You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="adm-page">
        <div className="adm-center">
          <p className="adm-muted">User not found.</p>
          <Link to="/admin/report"><button className="adm-btn adm-btn-secondary">← Back to Report</button></Link>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="adm-page">
        <div className="adm-center">
          <p className="adm-muted">Failed to load user report.</p>
          <button className="adm-btn adm-btn-secondary" onClick={fetchReport}>Retry</button>
        </div>
      </div>
    );
  }

  const { user, summary, gameBreakdown, recentSessions, eloSnapshot } = data;

  return (
    <div className="adm-page">

      {/* ── Header ── */}
      <div className="adm-header">
        <div>
          <h1 className="adm-title">{user.username}</h1>
          <div className="adm-user-meta">
            {user.email && <span className="adm-muted">{user.email}</span>}
            <span className={`adm-badge ${user.active ? "adm-badge-active" : "adm-badge-inactive"}`}>
              {user.active ? "Active" : "Disabled"}
            </span>
            {user.is_admin  && <span className="adm-badge adm-badge-admin">Admin</span>}
            {user.is_google && <span className="adm-badge adm-badge-google">Google</span>}
            {user.subscription_end && (
              <span className="adm-muted">
                Sub ends {fmtDate(user.subscription_end)}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Link to="/admin/report">
            <button className="adm-btn adm-btn-secondary">← Report</button>
          </Link>
          <Link to="/admin/users">
            <button className="adm-btn adm-btn-secondary">Users</button>
          </Link>
          <button className="adm-btn adm-btn-primary" onClick={fetchReport}>Refresh</button>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div className="rpt-summary">
        <SummaryCard label="Total plays"    value={summary.totalPlays}    color="blue"   />
        <SummaryCard label="Game plays"     value={summary.gamePlays}     color="purple" />
        <SummaryCard label="Vocab tests"    value={summary.vocabPlays}    color="green"  />
        <SummaryCard label="ELO games"      value={summary.eloPlays}      color="teal"   />
        <SummaryCard label="Coins"          value={summary.coins?.toLocaleString()} color="orange" />
        <SummaryCard label="Owls collected" value={summary.owlsCollected} color="pink"   />
        <SummaryCard label="Last active"    value={fmtDateShort(summary.lastActive)} color="yellow" />
      </div>

      {/* ── Game breakdown ── */}
      <section className="rpt-section">
        <h2 className="rpt-section-title">Activity by game</h2>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Game / Test</th>
                <th>Plays</th>
                <th>Avg score</th>
                <th>Last played</th>
              </tr>
            </thead>
            <tbody>
              {gameBreakdown.length === 0 && (
                <tr><td colSpan={4} className="adm-empty">No game data yet.</td></tr>
              )}
              {gameBreakdown.map((g) => (
                <tr key={g.type}>
                  <td><strong>{gameLabel(g.type)}</strong></td>
                  <td>{Number(g.plays).toLocaleString()}</td>
                  <td className="adm-muted-cell">
                    {g.avg_pct != null ? `${g.avg_pct}%` : "—"}
                  </td>
                  <td className="adm-muted-cell">{fmtDateTime(g.last_played)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── ELO Snapshot ── */}
      {eloSnapshot && (
        <section className="rpt-section">
          <h2 className="rpt-section-title">Vocabulary ELO</h2>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Game</th>
                  <th>Current ELO</th>
                  <th>Highest ELO</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Synonyms Game</strong></td>
                  <td>{eloSnapshot.synonyms_elo}</td>
                  <td>{eloSnapshot.synonyms_highest}</td>
                </tr>
                <tr>
                  <td><strong>Antonyms Game</strong></td>
                  <td>{eloSnapshot.antonyms_elo}</td>
                  <td>{eloSnapshot.antonyms_highest}</td>
                </tr>
                <tr>
                  <td><strong>Vocab Matching</strong></td>
                  <td>{eloSnapshot.vocab_elo}</td>
                  <td>{eloSnapshot.vocab_highest}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── Recent sessions ── */}
      <section className="rpt-section">
        <h2 className="rpt-section-title">Recent sessions</h2>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Game / Test</th>
                <th>Title</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {recentSessions.length === 0 && (
                <tr><td colSpan={4} className="adm-empty">No sessions yet.</td></tr>
              )}
              {recentSessions.map((s) => {
                const isElo = s.source === 'elo';
                const pct = !isElo && s.max_score
                  ? Math.round((s.score / s.max_score) * 100)
                  : null;
                return (
                  <tr key={`${s.source}-${s.id}`}>
                    <td className="adm-muted-cell">{fmtDateTime(s.played_at)}</td>
                    <td><strong>{gameLabel(s.type)}</strong></td>
                    <td className="adm-muted-cell">{s.title || "—"}</td>
                    <td>
                      {isElo
                        ? <span className="adm-muted-cell">ELO: {s.score}</span>
                        : s.score != null
                          ? <>{s.score}/{s.max_score}{pct != null && <span className="adm-muted-cell"> ({pct}%)</span>}</>
                          : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

    </div>
  );
}
